import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiCheck, FiChevronDown } from "react-icons/fi";
import {
  isDigitalKeyLocationItem,
  isOthersKeyLocationItem,
  type KeyLocationCatalogItem,
} from "../services/propertyKeyLocation";
import KeyLocationTableSpecialDraft from "./KeyLocationTableSpecialDraft";

export type KeyLocationTableSavePayload = {
  keyLocationId: number | null;
  keyLocationNote: string | null;
  keyLocationPassword?: string;
};

type Props = {
  options: KeyLocationCatalogItem[];
  valueId: number | null;
  valueNote?: string | null;
  valuePassword?: string | null;
  disabled?: boolean;
  saving?: boolean;
  onSave: (payload: KeyLocationTableSavePayload) => void;
  onNeedPassword?: () => Promise<string>;
};

export default function PropertyKeyLocationTableSelect({
  options,
  valueId,
  valueNote,
  valuePassword,
  disabled,
  saving,
  onSave,
  onNeedPassword,
}: Props) {
  const [open, setOpen] = useState(false);
  const [othersDraftOpen, setOthersDraftOpen] = useState(false);
  const [digitalDraftOpen, setDigitalDraftOpen] = useState(false);
  const [draftNote, setDraftNote] = useState(valueNote ?? "");
  const [draftPassword, setDraftPassword] = useState(valuePassword ?? "");
  const rootRef = useRef<HTMLDivElement>(null);
  const passwordRequestId = useRef(0);

  const selected = useMemo(
    () => options.find((o) => o.id === valueId) ?? null,
    [options, valueId]
  );

  const label = selected?.name ?? "—";
  const alreadyDigital = selected ? isDigitalKeyLocationItem(selected) : false;

  useEffect(() => {
    setDraftNote(valueNote ?? "");
    setDraftPassword(valuePassword ?? "");
  }, [valueNote, valueId, valuePassword]);

  const hydrateDigitalPassword = useCallback(async () => {
    const known = String(valuePassword ?? "").trim();
    if (known !== "") {
      setDraftPassword(known);
      return;
    }

    if (!onNeedPassword) {
      setDraftPassword("");
      return;
    }

    const requestId = passwordRequestId.current + 1;
    passwordRequestId.current = requestId;
    try {
      const nextPassword = await onNeedPassword();
      if (passwordRequestId.current !== requestId) return;
      setDraftPassword(nextPassword);
    } catch {
      if (passwordRequestId.current !== requestId) return;
      setDraftPassword("");
    }
  }, [onNeedPassword, valuePassword]);

  useEffect(() => {
    if (!open) {
      setOthersDraftOpen(false);
      setDigitalDraftOpen(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      const el = rootRef.current;
      if (!el || el.contains(e.target as Node)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  const close = useCallback(() => {
    setOpen(false);
    setOthersDraftOpen(false);
    setDigitalDraftOpen(false);
  }, []);

  const pickPlain = useCallback(
    (item: KeyLocationCatalogItem) => {
      if (disabled || saving) return;
      onSave({ keyLocationId: item.id, keyLocationNote: null });
      close();
    },
    [disabled, saving, onSave, close]
  );

  const othersOption = options.find((o) => isOthersKeyLocationItem(o)) ?? null;
  const digitalOption = options.find((o) => isDigitalKeyLocationItem(o)) ?? null;

  const submitOthers = useCallback(() => {
    if (disabled || saving || !othersOption) return;
    const note = draftNote.trim();
    if (!note) return;
    onSave({ keyLocationId: othersOption.id, keyLocationNote: note });
    close();
  }, [disabled, saving, othersOption, draftNote, onSave, close]);

  const submitDigital = useCallback(() => {
    if (disabled || saving || !digitalOption) return;
    const password = draftPassword.trim();
    if (!password && !alreadyDigital) return;
    onSave({
      keyLocationId: digitalOption.id,
      keyLocationNote: null,
      ...(password !== "" ? { keyLocationPassword: password } : {}),
    });
    close();
  }, [
    disabled,
    saving,
    digitalOption,
    draftPassword,
    alreadyDigital,
    onSave,
    close,
  ]);

  const showOthersDraft = !!othersOption && othersDraftOpen;
  const showDigitalDraft = !!digitalOption && digitalDraftOpen;

  return (
    <div
      ref={rootRef}
      className="relative flex w-full min-w-0 flex-col items-stretch justify-start py-1"
    >
      <button
        type="button"
        disabled={disabled || saving}
        onClick={() => {
          if (disabled || saving) return;
          setOpen((current) => {
            const next = !current;
            if (next) {
              setOthersDraftOpen(
                selected ? isOthersKeyLocationItem(selected) : false
              );
              const shouldOpenDigital = selected
                ? isDigitalKeyLocationItem(selected)
                : false;
              setDigitalDraftOpen(shouldOpenDigital);
              if (shouldOpenDigital) {
                void hydrateDigitalPassword();
              }
            }
            return next;
          });
        }}
        className={`flex min-h-[36px] w-full items-center justify-between gap-2 rounded-lg border-0 bg-transparent px-1 py-1.5 text-left text-sm font-medium text-slate-700 transition hover:bg-slate-900/[0.04] disabled:cursor-not-allowed disabled:opacity-50 ${
          open ? "text-slate-900 ring-1 ring-slate-300/80 ring-offset-0" : ""
        }`}
      >
        <span className="line-clamp-2 min-w-0 flex-1 text-left leading-snug">
          {saving ? "Salvando…" : label}
        </span>
        <FiChevronDown
          className={`h-4 w-4 shrink-0 text-slate-500 transition ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open ? (
        <div
          className="absolute right-0 top-full z-50 mt-1 w-[min(92vw,260px)] overflow-hidden rounded-xl border border-solid border-slate-200 bg-white text-left shadow-lg"
          role="listbox"
        >
          <div className="max-h-48 overflow-y-auto py-0.5">
            {options.map((item) => {
              const active = item.id === valueId;
              const isOthers = isOthersKeyLocationItem(item);
              const isDigital = isDigitalKeyLocationItem(item);
              return (
                <button
                  key={item.id}
                  type="button"
                  className={`flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-slate-50 ${
                    active ? "bg-orange-50/80 text-slate-900" : "text-slate-800"
                  }`}
                  onClick={() => {
                    if (disabled || saving) return;
                    if (isOthers) {
                      setDigitalDraftOpen(false);
                      setOthersDraftOpen(true);
                      setDraftNote(valueNote ?? "");
                      return;
                    }
                    if (isDigital) {
                      setOthersDraftOpen(false);
                      setDigitalDraftOpen(true);
                      void hydrateDigitalPassword();
                      return;
                    }
                    pickPlain(item);
                  }}
                >
                  <span className="flex w-4 shrink-0 justify-center">
                    {active && !isOthers && !isDigital ? (
                      <FiCheck className="h-4 w-4 text-orange-600" />
                    ) : null}
                  </span>
                  <span className="truncate">{item.name}</span>
                </button>
              );
            })}
          </div>

          {showOthersDraft ? (
            <KeyLocationTableSpecialDraft
              kind="others"
              value={draftNote}
              onChange={setDraftNote}
              disabled={disabled || saving}
              onSubmit={submitOthers}
            />
          ) : null}

          {showDigitalDraft ? (
            <KeyLocationTableSpecialDraft
              kind="digital"
              value={draftPassword}
              onChange={setDraftPassword}
              alreadySaved={alreadyDigital}
              disabled={disabled || saving}
              onSubmit={submitDigital}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
